/**
 * <RecommendationsPanel> — error-pattern recommendations tab in the
 * WorkloadDrawer (#260).
 *
 * Renders the curated registry's matches as cards: label + description +
 * a "View runbook" link to the GitHub blob URL. Visible to ALL roles —
 * recommendations are observable knowledge, not a privileged surface.
 *
 * Empty state intentionally points the operator at the Page-On-Call
 * button rather than implying "everything is fine" — a workload can be
 * degraded with no registry-known cause.
 */
import { ExternalLink, BookOpen, Info } from 'lucide-react';
import type { KnownErrorPattern } from '@/types';
import { useWorkloadRecommendations } from './useClusterData';

interface Props {
  siteId: string;
  namespace: string;
  kind: 'deployment' | 'statefulset' | 'daemonset';
  name: string;
}

// The runbook lives in the JSS repo on `main`. Until per-deployment
// runbook versioning ships (explicitly out of scope for v1), we link
// at HEAD on the default branch — operators always see the freshest
// triage steps.
const RUNBOOK_BASE_URL = 'https://github.com/Jeket-com/JSS/blob/main/';

function runbookUrl(path: string): string {
  // Path comes from the registry as `docs/runbooks/<file>.md`. We don't
  // url-encode segments because all curated paths are ASCII / no spaces.
  return `${RUNBOOK_BASE_URL}${path}`;
}

function PatternCard({ pattern }: { pattern: KnownErrorPattern }) {
  return (
    <div
      data-testid="recommendation-card"
      data-pattern-id={pattern.id}
      className="border border-surface-elevated rounded p-3 bg-surface-elevated/30 hover:bg-surface-elevated/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-silver-bright">{pattern.label}</h4>
          <p className="text-xs text-silver-muted mt-1 leading-relaxed">{pattern.description}</p>
        </div>
        <a
          data-testid="recommendation-runbook-link"
          href={runbookUrl(pattern.runbookPath)}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded bg-gold/10 text-gold border border-gold/30 hover:bg-gold/20 transition-colors"
        >
          <BookOpen size={12} aria-hidden="true" />
          View runbook
          <ExternalLink size={10} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}

export default function RecommendationsPanel({ siteId, namespace, kind, name }: Props) {
  const { data, isLoading, error } = useWorkloadRecommendations(
    siteId,
    namespace,
    kind,
    name,
  );

  if (isLoading && !data) {
    return (
      <div data-testid="recommendations-loading" className="space-y-2 px-2 py-2 animate-pulse">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-16 bg-surface-elevated rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="recommendations-error" className="px-2 py-2 text-xs text-status-error">
        Failed to load recommendations: {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div
        data-testid="recommendations-empty"
        className="text-center py-8 px-4 text-xs text-silver-muted"
      >
        <Info size={20} className="mx-auto text-silver-muted/40 mb-2" aria-hidden="true" />
        <p>No known issues detected.</p>
        <p className="mt-1 text-silver-muted/70">
          If the workload is degraded with no recommendations, escalate via the
          Page&nbsp;On-call button.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="recommendations-panel" className="px-2 py-2 space-y-2">
      {data.map((pattern) => (
        <PatternCard key={pattern.id} pattern={pattern} />
      ))}
    </div>
  );
}
